import { useEffect } from 'react';
import type { ViewStyle } from 'react-native';
import {
  type AnimatedStyle,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

/**
 * Entrance for a success checkmark: it fades up, tilts level, and overshoots its
 * size before settling. The overshoot is what makes it read as a stamp landing
 * rather than an icon appearing, so the four values move together and are kept
 * here rather than reproduced per surface.
 *
 * First written for the Medical On Demand submit confirmation; shared so the On
 * Your Honor reason sheet acknowledges an answer the same way. Honors
 * reduced-motion by snapping to the resting state, as `useEntranceAnimation`
 * does.
 *
 * @param delay Milliseconds before the entrance starts, to let a surrounding
 *   surface settle first.
 */
export function useSuccessCheckAnimation(delay = 120): AnimatedStyle<ViewStyle> {
  const opacity = useSharedValue(0);

  const scale = useSharedValue(0.35);

  const rotate = useSharedValue(-28);

  const translateY = useSharedValue(-6);

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) {
      opacity.value = 1;

      scale.value = 1;

      rotate.value = 0;

      translateY.value = 0;

      return;
    }

    opacity.value = withDelay(delay, withTiming(1, { duration: 180 }));

    scale.value = withDelay(
      delay,
      withSequence(
        withTiming(1.15, { duration: 220, easing: Easing.out(Easing.back(1.2)) }),
        withSpring(1, { damping: 11, stiffness: 240, mass: 0.4 })
      )
    );

    rotate.value = withDelay(
      delay,
      withTiming(0, { duration: 260, easing: Easing.out(Easing.cubic) })
    );

    translateY.value = withDelay(
      delay,
      withTiming(0, { duration: 220, easing: Easing.out(Easing.cubic) })
    );
  }, [opacity, scale, rotate, translateY, reducedMotion, delay]);

  return useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [
      { translateY: translateY.value },
      { scale: scale.value },
      { rotate: `${rotate.value}deg` },
    ],
  }));
}
